import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Control, Controller, FieldValues, useWatch } from 'react-hook-form'

import { themeColors } from '../../theme'
import { Field } from '../../utilities/Forms/types'

interface ExclusiveProviderMultiselectProps {
  field: Field
  options: any[]
  control: Control<FieldValues, any>
  excludeKeys: string[]
}

export const ExclusiveProviderMultiselect = ({
  field,
  options,
  control,
  excludeKeys,
}: ExclusiveProviderMultiselectProps) => {
  const excludedValues = useWatch({
    control,
    name: excludeKeys,
  })

  const excludedIds = new Set(
    (excludedValues || [])
      .flat()
      .filter(Boolean)
      .map((v: any) =>
        typeof v === 'object' ? v.apiId || v.value || v.providerId : v
      )
  )

  const filteredOptions = options.filter(
    (opt: any) => !excludedIds.has(opt.apiId || opt.value || opt)
  )

  return (
    <Controller
      name={field.backendKey}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Box>
          <Autocomplete
            multiple
            options={filteredOptions}
            sx={{ width: 'auto', height: 'min-content' }}
            getOptionLabel={(option: any) => `${option.label}`}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((val, index) => {
                const option = options.find((opt) =>
                  field.chipLabelKey
                    ? opt[field.chipLabelKey] === val[field.chipLabelKey] ||
                      opt.apiId === val
                    : opt.apiId === val
                )
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${field.backendKey}-${index}`}
                    label={
                      option
                        ? field.chipLabelKey
                          ? (option as any)[field.chipLabelKey]
                          : option.label
                        : ''
                    }
                  />
                )
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                id={field.backendKey}
                name={field.backendKey}
                size='small'
                sx={{ height: 'min-content' }}
                variant='outlined'
                className={!field.isEditable ? 'Mui-disabled' : ''}
                inputProps={{
                  ...params.inputProps,
                  readOnly: !field.isEditable,
                }}
                error={!!error}
              />
            )}
            value={value || []}
            defaultValue={value || []}
            onChange={(_, data) =>
              onChange(
                [...data].map((d) =>
                  !field.chipLabelKey ? d.apiId || d.value || d : d
                )
              )
            }
            isOptionEqualToValue={(opt, val) =>
              (!field.chipLabelKey ? opt.apiId || opt.value || opt : opt) ===
              val
            }
          />
          {error && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: themeColors.red,
              }}
            >
              {error.message}
            </Typography>
          )}
        </Box>
      )}
    />
  )
}
